import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import logger from "../utils/logging";
import UserAuthService from "../services/UserAuthService";
import sweapLogo from "../img/sweap-logo.svg";

interface LoginProps {
    authService: UserAuthService;
}

const Login: React.FC<LoginProps> = (props) => {
    const { authService } = props;
    const { t } = useTranslation(["auth", "common"]);

    const [isLoading, setLoading] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string>("");

    const navigate = useNavigate();
    const location = useLocation();

    const checkAndRedirect = () => {
        if (authService.isInitialized() && authService.isLoggedIn()) {
            const from = location.state?.from?.pathname
                ? location.state.from.pathname + (location.state.from.search || "")
                : "/";
            navigate(from, { replace: true });
        }
    };

    useEffect(() => {
        const unsubscribeInit = authService.onInitializationChanged(checkAndRedirect);
        const unsubscribeAuth = authService.onAuthStateChanged(checkAndRedirect);

        // Check immediately
        checkAndRedirect();

        return () => {
            unsubscribeInit();
            unsubscribeAuth();
        };
    }, [authService, navigate, location.state]);

    const handleSubmit = () => {
        setLoading(true);

        // Get the intended redirect location from ProtectedRoute
        const from = location.state?.from?.pathname + (location.state?.from?.search || "") || "/";

        authService
            .login(from)
            .then(() => {
                logger.info("keycloak login finished");
                setLoading(false);
                if (authService.isLoggedIn()) {
                    // If login completed without redirect (silent SSO), navigate directly
                    navigate(from);
                } else {
                    setLoginError(t("errors.loginFailed"));
                }
            })
            .catch((error: unknown) => {
                logger.error("keycloak login failed", error);
                setLoginError(getErrorMessage(error));
                setLoading(false);
            });
    };

    function getErrorMessage(error: unknown): string {
        if (!error) {
            return t("errors.unknownError");
        }
        if (error instanceof Error) {
            return error.message;
        }
        return JSON.stringify(error);
    }

    return (
        <div className="login-view d-flex flex-column justify-content-center align-items-center text-center">
            <div className="login-content w-100">
                <img src={sweapLogo} alt="Sweap" className="login-logo mb-3" />
                <h1 className="login-title mb-2">{t("common:base.title")}</h1>
                <p className="text-body-secondary mb-4">{t("login.subtitle")}</p>
                {!isLoading ? (
                    <>
                        <Button type="submit" variant="primary" onClick={handleSubmit} className="w-100">
                            {t("login.button")}
                        </Button>
                        {loginError && (
                            <Alert
                                variant="danger"
                                onClose={() => setLoginError("")}
                                dismissible
                                className="mt-3 text-start"
                            >
                                <Alert.Heading>{t("login.errorTitle")}</Alert.Heading>
                                <p>{loginError}</p>
                            </Alert>
                        )}
                    </>
                ) : (
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("login.loading")}</span>
                    </Spinner>
                )}
            </div>
        </div>
    );
};

export default Login;
